/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import { Link as RouterLink } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Tooltip from "@mui/material/Tooltip";
import { CardHeader } from "@mui/material";
import { HelpOutlined } from "@mui/icons-material";
import ChevronRight from "@mui/icons-material/ChevronRight";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import DraftsIcon from "@mui/icons-material/Drafts";
import { deepOrange, teal } from "@mui/material/colors";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import { IntegratedSystemDetail } from "../../api/integratedsystems";
import { NUMBER_FORMATTER } from "../utils/utils";

interface CRMOverviewListItemProps {
  fact: number;
  header: string;
  tooltip: string;
  icon: React.ReactNode;
  iconColor?: string;
  /** When set, the row body is a link to this path */
  to?: string;
  /** Accessible name of the link */
  linkLabel?: string;
}

function CRMOverviewListItem({
  fact,
  header,
  tooltip,
  icon,
  iconColor,
  to,
  linkLabel,
}: Readonly<CRMOverviewListItemProps>) {
  const iconBlock = (
    <>
      <div
        style={{
          width: "30px",
          height: "30px",
          marginRight: "5px",
          alignSelf: "center",
          color: iconColor,
        }}
      >
        {icon}
      </div>

      <Divider orientation="vertical" flexItem />
    </>
  );

  const factBlock = (
    <div>
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
        {header}
      </Typography>
      <Typography
        variant="body2"
        className={to ? "crm-overview-fact" : undefined}
        sx={{ fontWeight: "bold", color: to ? "primary.main" : "#777776" }}
      >
        {NUMBER_FORMATTER.format(fact)}
      </Typography>
    </div>
  );

  const helpIcon = (
    <Tooltip title={tooltip}>
      <HelpOutlined style={{ width: "15px", height: "15px" }} />
    </Tooltip>
  );

  const factContainerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: "10px",
    flex: 1,
  };

  if (!to) {
    return (
      <ListItem disableGutters sx={{ py: 0.5 }}>
        <div style={{ display: "flex", width: "100%" }}>
          {iconBlock}

          <div style={factContainerStyle}>
            {factBlock}

            {helpIcon}
          </div>
        </div>
      </ListItem>
    );
  }

  return (
    <ListItem disableGutters sx={{ py: 0.5 }}>
      <ListItemButton
        component={RouterLink}
        to={to}
        aria-label={linkLabel}
        disableGutters
        sx={{
          p: 0,
          flex: 1,
          "&:hover, &.Mui-focusVisible": { backgroundColor: "action.hover" },
          "&.Mui-focusVisible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: "-2px",
          },
          "&:hover .crm-overview-fact, &.Mui-focusVisible .crm-overview-fact": {
            textDecoration: "underline",
          },
        }}
      >
        {iconBlock}

        <div style={factContainerStyle}>
          {factBlock}

          <ChevronRight aria-hidden fontSize="small" />
        </div>
      </ListItemButton>

      {helpIcon}
    </ListItem>
  );
}

interface CRMOverviewPanelProps {
  systemDetails: IntegratedSystemDetail;
  /** When set, the panel is rendered for a data project and Total Contacts links to its contacts page */
  dataProjectId?: number;
}

export const CRMOverviewPanel: React.FC<CRMOverviewPanelProps> = ({
  systemDetails,
  dataProjectId,
}) => {
  if (!systemDetails?.crm_stats) {
    return null;
  }

  const uniqueContacts =
    systemDetails.crm_stats.total_contacts > 0
      ? systemDetails.crm_stats.total_contacts -
        (systemDetails.merged_contacts?.tickets || 0)
      : 0;

  return (
    <MiraCard sx={{ height: "100%" }}>
      <CardHeader title="CRM Overview" />
      <MiraCardContent>
        <List dense>
          <CRMOverviewListItem
            fact={systemDetails.crm_stats.total_contacts}
            header="Total Contacts"
            tooltip={
              dataProjectId
                ? "Total contacts available in your CRM. Click to review them."
                : "Total contacts available in your CRM"
            }
            icon={<AccountBoxIcon style={{ width: "30px", height: "30px" }} />}
            to={
              dataProjectId
                ? `/data-project/contacts/${dataProjectId}`
                : undefined
            }
            linkLabel={`Total Contacts, ${NUMBER_FORMATTER.format(
              systemDetails.crm_stats.total_contacts
            )} — view contacts in this data project`}
          />
          <CRMOverviewListItem
            fact={uniqueContacts}
            header="Unique Contacts"
            tooltip="Total Contacts less duplicates"
            icon={<AccountBoxIcon style={{ width: "30px", height: "30px" }} />}
            iconColor={teal[800]}
          />
          <CRMOverviewListItem
            fact={systemDetails.crm_stats.orphaned_contacts}
            header="Orphaned Contacts"
            tooltip="Contacts in your CRM that have no Account"
            icon={<AccountBoxIcon style={{ width: "30px", height: "30px" }} />}
            iconColor={deepOrange[600]}
          />
          <CRMOverviewListItem
            fact={systemDetails.crm_stats.companies_count?.crm || 0}
            header="Total Companies"
            tooltip="Total Accounts available in your CRM"
            icon={<BusinessIcon style={{ width: "30px", height: "30px" }} />}
          />
          <CRMOverviewListItem
            fact={systemDetails.crm_stats.companies_count?.cirrom || 0}
            header="Unique Companies"
            tooltip="Accounts Cirrom considers to be unique. It could be lower (eg. duplicate company names) or higher (eg. multiple domains associated with the same account) than the CRM account number"
            icon={<BusinessIcon style={{ width: "30px", height: "30px" }} />}
            iconColor={teal[800]}
          />
          {systemDetails.email_stats &&
          systemDetails.email_stats.sent_emails > 0 ? (
            <CRMOverviewListItem
              fact={systemDetails.email_stats.sent_emails}
              header="Sent Email"
              tooltip="Sent emails in the last 90 days"
              icon={<EmailIcon style={{ width: "30px", height: "30px" }} />}
            />
          ) : null}
          {systemDetails.email_stats &&
          systemDetails.email_stats.opened_emails > 0 ? (
            <CRMOverviewListItem
              fact={systemDetails.email_stats.opened_emails}
              header="Opened Email"
              tooltip="Opened emails in the last 90 days"
              icon={<DraftsIcon style={{ width: "30px", height: "30px" }} />}
              iconColor={teal[800]}
            />
          ) : null}
        </List>
      </MiraCardContent>
    </MiraCard>
  );
};
